import { faiService } from '@services/fai';
import { getLocalVariable, setLocalVariable } from '@services/storage';

import { SapicCategoryId, SapicFundingMode } from '@app-types/api/sapic';
import { EligibilityConfigType } from '@app-types/config';
import { EligibilityOptionType, EligibilityResult, ProviderSummary } from '@app-types/eligibility';
import { UpsellItemsConfiguration } from '@app-types/eligibility/config';
import { ResultsType } from '@app-types/eligibility/results';
import { ProductType } from '@app-types/product';
import { UserType } from '@app-types/user';

import { TYPE_ELIGIBILITY } from '@constants/eligibility';
import { CATEGORY_TYPE, ELIGIBILITY_STORAGE_KEY, ERROR_CONTEXT, TECHNOLOGY } from '@constants/provider';

import { getOptions } from '@eligibility/actions';

import { EligibilityOffer, EligibilityOfferFactory, RawEligibilityOffer } from './eligibility-offer';
import { AddressType, EligibilityResultType } from './eligibility-widget';
import { JourneyConfigType } from './journey-config';

const technologiesMapper: Record<string, TECHNOLOGY> = {
    ftth: TECHNOLOGY.FTTH,
    box4g: TECHNOLOGY.BOX_4G,
    box5g: TECHNOLOGY.BOX_5G,
};

const priorityTechnologies: TECHNOLOGY[] = [TECHNOLOGY.FTTH, TECHNOLOGY.BOX_5G, TECHNOLOGY.BOX_4G];

export interface SavedEligibilityResultType {
    idPanier: string;
    eligType: TYPE_ELIGIBILITY;
    address: {
        codePostal: string;
        voie: string;
        commune: string;
        numeroVoie: string;
        extension: string;
        nd?: string;
        pto?: string;
        inlineSplit: string[];
        inline: string;
    };
    technicalAddress: AddressType;
    housing: EligibilityResultType['housing'];
    offersWithOptions: EligibilityResultType['offersWithOptions'];
    results: ResultsType;
    techno?: TECHNOLOGY;
    userId: string | null;
    mustGetOffers: boolean;
    offers: RawEligibilityOffer[];
    error: ERROR_CONTEXT | null;
}

interface BypassEligibilityResultType extends SavedEligibilityResultType {
    bypass: true;
}

export class EligibilityResults {
    public readonly results: ResultsType;
    private readonly cartId: string;
    private readonly type: TYPE_ELIGIBILITY;
    private readonly technicalAddress: AddressType;
    private readonly _address: {
        codePostal: string;
        voie: string;
        commune: string;
        numeroVoie: string;
        pto?: string;
        extension: string;
        nd?: string;
        inlineSplit: string[];
        inline: string;
    };
    private readonly housing: EligibilityResultType['housing'];
    private readonly offersWithOptions: EligibilityResultType['offersWithOptions'];

    private readonly _technology?: TECHNOLOGY;
    private shouldGetOffers: boolean = true;
    public readonly user?: UserType;
    private readonly expiresIn = new Date().setDate(new Date().getDate() + 1);
    private _isReady: boolean = false;
    private _offers: { [key in TECHNOLOGY]?: EligibilityOffer[] } = {};
    private readonly journeyConfig: JourneyConfigType;
    private readonly eligibilityConfig: EligibilityConfigType;
    private groupKeyCache: { [key: string]: string } = {};
    private _error: ERROR_CONTEXT | null = null;

    constructor(
        type: TYPE_ELIGIBILITY,
        result: EligibilityResultType | BypassEligibilityResultType,
        configs: {
            eligibilityConfig: EligibilityConfigType;
            journeyConfig: JourneyConfigType;
        },
        user?: UserType,
        callback?: (eligibilityResult: EligibilityResults) => void,
    ) {
        this.type = type;
        this.user = user;
        this.eligibilityConfig = configs.eligibilityConfig;
        this.journeyConfig = configs.journeyConfig;

        let offersAvailable: EligibilityOffer[] = [];

        if ('bypass' in result) {
            this.cartId = result.idPanier;
            this.technicalAddress = result.technicalAddress;
            this._address = result.address;
            this.housing = result.housing;
            this.offersWithOptions = result.offersWithOptions;
            this.results = result.results;
            this._technology = result.techno;
            this.shouldGetOffers = result.mustGetOffers;

            offersAvailable = result.offers.map((offer) => {
                return new EligibilityOffer(offer, configs.journeyConfig.productsToUpsell, offer.speedOption);
            });
            this._error = result.error;
        } else {
            this.cartId = result.id;
            this.technicalAddress = result.address;
            this._address = this.addressMapper(result.address, result.searchData);
            this.housing = result.housing;
            this.offersWithOptions = result.offersWithOptions;
            this.results = this.resultsMapper(result);
            this._technology = this.getTechnology(this.results);
        }

        this.processOffers(offersAvailable)
            .then((offers) => {
                if (this.hasMismatch(offers)) {
                    this._error = ERROR_CONTEXT.MISMATCH_CONFIG;
                }

                this._offers = offers;
            })
            .catch(() => {
                this._offers = {};
            })
            .finally(() => {
                this._isReady = true;
                callback?.(this);
            });
    }

    public get technology() {
        return this._technology;
    }

    public get isReady() {
        return this._isReady;
    }

    public get offers() {
        if (!this.isReady) {
            throw new Error('Offers are not ready yet');
        }

        return this._offers;
    }

    public get address() {
        return this._address;
    }

    public get error() {
        return this._error;
    }

    private addressMapper(address: AddressType, searchData: EligibilityResultType['searchData']) {
        const {
            streetNumber: { number: streetNumber, complement },
            street: { label: streetLabel },
            zipCode,
            city: { label: cityLabel },
        } = address;

        const inlineSplit = [
            `${streetNumber ?? ''} ${complement ?? ''} ${streetLabel ?? ''}`,
            `${zipCode ?? ''} ${cityLabel ?? ''}`,
        ];
        const inline = inlineSplit.join(' ');

        return {
            codePostal: address.zipCode,
            voie: address.street.label,
            commune: address.city.label,
            numeroVoie: address.streetNumber.number,
            extension: address.streetNumber.complement,
            nd: searchData?.nd ?? '',
            pto: searchData?.pto,
            inlineSplit,
            inline,
        };
    }

    private resultsMapper(result: EligibilityResultType) {
        return {
            ftth: {
                offers: result.ftth?.offers ?? [],
                status: result.ftth?.status ?? false,
                technology: result.ftth?.technoType,
                forecast: result.ftth?.forecast,
                buildings: result.ftth?.buildings,
                shouldBypass: result.ftth?.shouldBypass ?? false,
                collectionNetwork: result.ftth?.collectionNetwork,
                groupCategoryHousingConnection: result.ftth?.groupeCategorieRaccoLogement,
                housingType: result.ftth?.housingType,
                connectionPointLocationType: result.ftth?.pmLocationType,
                commercialOpeningDate: result.ftth?.commercialOpeningDate,
                pto: result.ftth?.pto,
            },
            box4g: {
                offers: result.box4g.offers,
                status: result.box4g.status,
                technology: result.box4g.technoType,
                homeZone: result.box4g.homeZone,
            },
            box5g: {
                offers: result.box5g.offers,
                status: result.box5g.status,
                technology: result.box5g.technoType,
                homeZone: result.box5g.homeZone,
            },
        };
    }

    private getTechnology(results: ResultsType): TECHNOLOGY | undefined {
        const availableTechnologies: TECHNOLOGY[] = [];

        Object.entries(results).forEach(([key, { status, offers }]: [string, EligibilityResult]) => {
            if (status && offers.length > 0) {
                availableTechnologies.push(technologiesMapper[key]!);
            }
        });

        const technology = priorityTechnologies.find((t) => availableTechnologies.includes(t));

        if (technology === TECHNOLOGY.BOX_5G) {
            return TECHNOLOGY.BOX_4G;
        }

        return technology;
    }

    private getEligibleOffers(): {
        eligibleOffers: string[];
        eligibilityOptions: EligibilityResultType['offersWithOptions'];
    } {
        let eligibleOffers: string[] = [];
        let eligibilityOptions: EligibilityResultType['offersWithOptions'] = [];

        if (!this.results) {
            return { eligibleOffers: [...new Set(eligibleOffers)], eligibilityOptions };
        }

        if (this.results.box5g.status) {
            eligibleOffers.push(...(this.results.box5g.offers || []));
        }

        if (this.results.ftth.status) {
            eligibleOffers = [...eligibleOffers, ...this.results.ftth.offers];
            eligibilityOptions = [...eligibilityOptions, ...this.offersWithOptions];
        } else {
            // Check 4G Box eligibility only if not eligible to FTTH
            if (this.results.box4g.status) {
                eligibleOffers.push(...(this.results.box4g.offers || []));
            }
        }

        return { eligibleOffers: [...new Set(eligibleOffers)], eligibilityOptions };
    }

    private async getOptionsDetails(
        eligibleOptions: EligibilityResultType['offersWithOptions'],
    ): Promise<Record<string, EligibilityOptionType>> {
        const optionsIds = [...new Set(eligibleOptions.flatMap(({ options }) => options))];

        if (optionsIds.length === 0) {
            return {};
        }

        const optionsData = await getOptions(optionsIds);

        const optionsDetails: Record<string, EligibilityOptionType> = {};

        eligibleOptions.forEach(({ idOffer, options }) => {
            const option = options.map((optionId) => optionsData[optionId]).filter((option) => option !== undefined);

            if (option.length) {
                const currentOption = option[0]!;
                optionsDetails[idOffer] = {
                    speed_down: `${Number(currentOption.speed_down!.split(' ')[0]) / 1000000}\u00A0Gb/s`,
                    speed_up: `${Number(currentOption.speed_up!.split(' ')[0]) / 1000000}\u00A0Gb/s`,
                    price: currentOption.price,
                };
            }
        });

        return optionsDetails;
    }

    private async getOffersDetails(): Promise<EligibilityOffer[]> {
        try {
            const { eligibleOffers, eligibilityOptions } = this.getEligibleOffers();

            // If no offers are eligible, return an empty array
            if (eligibleOffers.length <= 0) {
                return [];
            }
            const productsPromise = faiService.getOffers(
                {
                    gencodes: eligibleOffers,
                    fundingMode: SapicFundingMode.NONE,
                    limit: -1,
                    filterByTypes: [SapicCategoryId.FAI, SapicCategoryId.FAIM_UNLIMITED],
                    excludeCategoryType: CATEGORY_TYPE.OLD,
                },
                undefined,
                undefined,
                undefined,
                true,
            );

            const optionsPromise = this.getOptionsDetails(eligibilityOptions);

            const [productsResult, optionsResult] = await Promise.allSettled([productsPromise, optionsPromise]);

            if (productsResult.status === 'rejected') {
                throw new Error(productsResult.reason);
            }

            const offersAvailable = productsResult.status === 'fulfilled' ? productsResult.value : [];
            const options = optionsResult.status === 'fulfilled' ? optionsResult.value : {};

            const offers = offersAvailable.map((offer) =>
                EligibilityOfferFactory.createFromOffer(
                    offer,
                    this.journeyConfig.productsToUpsell,
                    options[offer.gencode],
                ),
            );

            this.shouldGetOffers = false;

            return offers;
        } catch (e) {
            this._error = ERROR_CONTEXT.ERROR;
            throw e;
        }
    }

    private filterOffers(offersAvailable: EligibilityOffer[]): EligibilityOffer[] {
        const technoToDisplayIfAlone = this.eligibilityConfig?.technoToDisplayIfAlone;
        return offersAvailable.filter((offer, index, allOffers) => {
            if (!offer.in_stock) {
                return false;
            }

            if (
                !(
                    offer.fai_category_type === this.journeyConfig.category ||
                    (this.journeyConfig.allowBox4g && offer.type === ProductType.FAIM_UNLIMITED)
                )
            ) {
                return false;
            }

            if (
                offer.technology === technoToDisplayIfAlone &&
                allOffers.some((offerObject) => offerObject.technology !== technoToDisplayIfAlone)
            ) {
                return false;
            }

            if (
                !!this.journeyConfig.offerCategoryCodeToShow &&
                !offer.categories.includes(this.journeyConfig.offerCategoryCodeToShow)
            ) {
                if (
                    this.journeyConfig.fallbackMismatchOffers &&
                    offer.categories.includes(this.journeyConfig.fallbackMismatchOffers)
                ) {
                    // to bypass the filter, in order to show the offer in case of mismatch (for instance show 4G/5G box on test-eligibilite-panier-mixte if no FTTH offer)
                    offer.isFallbackMismatchOffer = true;
                    return true;
                }
                return false;
            }

            return !(
                this.journeyConfig.offerCategoryCodeToHide?.length > 0 &&
                offer.categories.some((category) => this.journeyConfig.offerCategoryCodeToHide.includes(category))
            );
        });
    }

    private getGroupKey(offer: EligibilityOffer): string {
        if (!this.groupKeyCache[offer.sku]) {
            const keyArr: string[] = [];

            this.journeyConfig.groupOffersBy.forEach((attr) => {
                keyArr.push(offer.get(attr as keyof EligibilityOffer) as string);
            });
            this.groupKeyCache[offer.sku] = keyArr.join('_');
        }
        return this.groupKeyCache[offer.sku]!;
    }

    private groupOffers(offers: EligibilityOffer[]): { [key in TECHNOLOGY]?: EligibilityOffer[] } {
        // Regroupement des offres par technology par range-ng et par série commerciale
        const groupedOffers: { [key in TECHNOLOGY]?: EligibilityOffer[] } = {};

        offers.forEach((offer) => {
            let techno: TECHNOLOGY = offer.technology;

            if (offer.technology === TECHNOLOGY.BOX_5G) {
                techno = TECHNOLOGY.BOX_4G;
            }

            if (!groupedOffers[techno]) {
                groupedOffers[techno] = [];
                groupedOffers[techno]!.push(offer);
            } else if (!groupedOffers[techno]!.filter((o) => this.getGroupKey(o) === this.getGroupKey(offer)).length) {
                groupedOffers[techno]!.push(offer);
            }
        });

        return groupedOffers;
    }

    private hideUpsellIfBasePresent(offers: EligibilityOffer[], upsellProducts: UpsellItemsConfiguration[]) {
        offers.forEach((upsellOffer, _, allOffers) => {
            const upsellCombination = upsellProducts.find(({ upselledProduct }) => {
                return (
                    upselledProduct &&
                    upsellOffer.isSameRangeAndPlay(
                        upselledProduct.rangeNg,
                        upselledProduct.commercialSeries,
                        upselledProduct.play,
                    )
                );
            });

            if (!upsellCombination) {
                return;
            }

            const baseOffer = allOffers.find((offer) => {
                return offer.isSameRangeAndPlay(
                    upsellCombination.baseProduct.rangeNg,
                    upsellCombination.baseProduct.commercialSeries,
                    upsellCombination.baseProduct.play,
                );
            });

            if (baseOffer) {
                upsellOffer.isHidden = true;
            }
        });
    }

    private async processOffers(offersAvailable: EligibilityOffer[] = []) {
        if (this.shouldGetOffers) {
            offersAvailable = await this.getOffersDetails();
            this.save(offersAvailable);
        }

        const filteredOffers = this.filterOffers(offersAvailable);

        if (filteredOffers.length === 0 && !this.error) {
            this._error = ERROR_CONTEXT.NO_OFFER;
        }

        this.hideUpsellIfBasePresent(filteredOffers, this.journeyConfig.productsToUpsell);

        return this.groupOffers(filteredOffers);
    }

    private hasMismatch(offers: { [key in TECHNOLOGY]?: EligibilityOffer[] }): boolean {
        const availableTechnologiesForJourney = this.journeyConfig.technologies;
        if (!availableTechnologiesForJourney) {
            return false;
        }

        return !Object.values(offers)
            .flat()
            .some((offer) => {
                return availableTechnologiesForJourney.includes(offer.technology);
            });
    }

    private save(offersAvailable: EligibilityOffer[]) {
        const data: SavedEligibilityResultType = {
            idPanier: this.cartId,
            eligType: this.type,
            address: this.address,
            technicalAddress: this.technicalAddress,
            housing: this.housing,
            offersWithOptions: this.offersWithOptions,
            results: this.results,
            techno: this.technology,
            userId: this.user?.sub ?? null,
            mustGetOffers: this.shouldGetOffers,
            offers: offersAvailable,
            error: this.error,
        };

        const eligibleOffers = offersAvailable.reduce((acc, currentOffer) => {
            if (currentOffer.fai_category_type !== CATEGORY_TYPE.PRO) {
                const rangeNgCommercialSeries = currentOffer.rangeNg + currentOffer.commercialSeries;
                const techno = currentOffer.technology;
                const key = `${rangeNgCommercialSeries}_${techno}`.toLowerCase();
                acc.push(key);
            }

            return acc;
        }, [] as string[]);

        const summary: ProviderSummary = {
            address: {
                inline: this.address.inline,
                split: this.address.inlineSplit,
            },
            eligibleOffers,
        };

        setLocalVariable(
            ELIGIBILITY_STORAGE_KEY.RESULTS,
            JSON.stringify({
                data,
                expiresTimestamp: this.expiresIn,
            }),
        );

        setLocalVariable(
            ELIGIBILITY_STORAGE_KEY.ADDRESS,
            JSON.stringify({
                data: this.address,
                expiresTimestamp: this.expiresIn,
            }),
        );

        setLocalVariable(
            ELIGIBILITY_STORAGE_KEY.MODE,
            JSON.stringify({
                data: 'corbis',
                expiresTimestamp: this.expiresIn,
            }),
        );

        setLocalVariable(
            ELIGIBILITY_STORAGE_KEY.SUMMARY,
            JSON.stringify({
                data: summary,
                expiresTimestamp: this.expiresIn,
            }),
        );
    }

    public static fromLocalStorage(
        configs: {
            eligibilityConfig: EligibilityConfigType;
            journeyConfig: JourneyConfigType;
        },
        user?: UserType,
        callback?: (eligibilityResult: EligibilityResults) => void,
    ): EligibilityResults {
        const storedData = getLocalVariable(ELIGIBILITY_STORAGE_KEY.RESULTS);
        if (!storedData) {
            throw new Error('No data found in local storage');
        }

        const { data, expiresTimestamp } = JSON.parse(storedData) as {
            data: SavedEligibilityResultType;
            expiresTimestamp: number;
        };

        if (expiresTimestamp < new Date().getTime()) {
            throw new Error('Data has expired');
        }

        const shouldGetOffers = data.mustGetOffers || user?.sub !== (data.userId ?? undefined);

        return new EligibilityResults(
            data.eligType,
            { ...data, bypass: true, mustGetOffers: shouldGetOffers },
            configs,
            user,
            callback,
        );
    }

    public static clear() {
        setLocalVariable(ELIGIBILITY_STORAGE_KEY.RESULTS, '');
        setLocalVariable(ELIGIBILITY_STORAGE_KEY.ADDRESS, '');
        setLocalVariable(ELIGIBILITY_STORAGE_KEY.MODE, '');
        setLocalVariable(ELIGIBILITY_STORAGE_KEY.SUMMARY, '');
    }
}
